/**
 * RadarDomainTiles.tsx — نطاق رادار واحد **ببلاطات** بدل صورة كاملة.
 *
 * ── لماذا عاد التقطيع بعد أن رفضناه ──────────────────────────────────────────
 * رفضنا في 2026-09-07 بلاطات **RainViewer**، ولسببٍ لم يتغيّر: كان المزوّد يردّ فوق
 * ‏z=7 صورةً مكتوباً عليها «Zoom Level Not Supported» — صورة صالحة لا خطأ شبكة، فلا
 * يمنعها معالج أخطاء. وهذا عيب **ذلك المزوّد**، لا عيب البلاطات.
 *
 * وبلاطاتنا لا تملك تلك الحالة أصلاً: `maxzoom` مُعلَن، وما فوقه يُمطّطه MapLibre من
 * آخر مستوًى موجود — وهو بالضبط ما كان مصدر `image` يفعله، إلا أنه كان يفعله
 * **بالقارّة كلّها في كل لحظة**.
 *
 * ── ما الذي يشتريه التقطيع ───────────────────────────────────────────────────
 * الإطار الكامل 4096×4096 = 16.8 ميغابكسل (‏67 م.ب بعد فكّ الترميز)، يُرفع نسيجاً
 * مهما كان تكبير المستخدم. عالجنا ذلك بنسخة مصغَّرة أثناء الحركة (سُدس عشر الكلفة)،
 * لكنّ العيب الأصلي بقي: **الكلفة تتبع القارّة لا الشاشة**. هنا تتبع الشاشة: بضع
 * بلاطات 256×256 لما يُرى وحده، بدقّته الكاملة، وبعددٍ ثابت مهما بلغ التكبير.
 *
 * ── التبادل بين خانتين (ping-pong) ───────────────────────────────────────────
 * `setTiles` يُفرغ كاش البلاطات ويُعيد التحميل، فلو بدّلنا مصدر الإطار **المعروض**
 * لومض فارغاً. فنُبقي خانتين ثابتتين ونُسند إليهما الإطارين: الخانة التي خرج إطارها
 * من الزوج هي وحدها التي تُعاد تهيئتها، وهي مخفيّة (شفافية 0) ولها مدّة إطارٍ كامل
 * لتُحمّل قبل أن يأتي دورها. فلا وميض ولا إعادة تحميل لما يُعرض.
 *
 * ── الشفافية والمزج ──────────────────────────────────────────────────────────
 * كما في مصدر الصورة تماماً: ذوبان متقاطع (‏1−k و k لا 1 و k، لأن صور الرادار شفّافة
 * حيث لا صدى فتظلّ العاصفة المنقضية ظاهرة من تحتها)، ومدفوعٌ بساعة التشغيل لا بحالة
 * React — راجع RadarTileLayer.tsx للقياس الذي فرض ذلك.
 */

import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { framesAround } from './hooks/useRadarFrames';
import { getPlaybackPosition, subscribePlayback, type PlaybackPosition } from '../../services/playbackClock';
import { useMapRef } from './MapContext';
import {
    RADAR_VALUES_TILE_SIZE, radarTilesTemplate, radarValuesTemplate, type RadarDomain,
} from '../../services/mrmsRadarService';
import { radarReliefColor } from '../../config/radarPalette';
import {
    radarDebugContext, radarDebugLoaded, radarDebugOn, radarDebugOpacity, radarDebugSetTiles, radarDebugTick,
} from '../../services/radarDebug';

/** يطابق `MAX_Z` في backend/scripts/radar_tiles.py — وما فوقه يمطّطه MapLibre. */
const MAX_TILE_Z = 7;

/*
 * ── لماذا أربع خانات لا اثنتان ───────────────────────────────────────────────
 * كل خطوة تُجبر خانةً على `setTiles`، وMapLibre عندها يُفرغ كاش بلاطات ذلك المصدر
 * ويُعيد الجلب والفكّ والرفع إلى الـGPU. وبخانتين لا تملك الخانة المحرَّرة إلا **مدّة
 * إطارٍ واحد** (450 مللي) لتُنجز ذلك قبل أن يأتي دورها — وبعشرين نطاقاً يصير ذلك
 * عشرين إعادة تحميل في كل 450 مللي. فما يتأخّر يُعرض بعد بدء دوره: تقطيعٌ مقيس في
 * 2026-09-20 (‏17 توقّفاً في عشر ثوانٍ، أطولها 1.2 ثانية).
 *
 * بأربع خانات يُهيَّأ الإطار قبل عرضه بثلاث خطوات (1.35 ثانية)، فيصل قبل موعده بدل
 * أن يلهث خلفه. والثمن أربعة مصادر لكل نطاق بدل اثنين — ورخيصةٌ لأن كلّاً منها بضع
 * بلاطات 256 لا قارّة كاملة، وهو بالضبط ما جعل التقطيع يستحقّ العناء.
 */
const SLOTS = 4;

/*
 * ── عتامة الانتظار: غير صفرية، وغير مرئية ──────────────────────────────────────
 *
 * MapLibre لا يُحمّل بلاطات طبقةٍ عتامتها صفر — كما لا يُحمّل طبقةً مخفيّة. عُرف
 * الثاني في 2026-09-20 فانتقلنا إلى العتامة الصفرية ظنّاً أنها تُحمَّل. ثم أثبتت
 * لقطات النبضة في 2026-09-21 أنها لا تُحمَّل أيضاً: خانةٌ أُسندت قبل دورها بثلاث
 * خطوات بقيت `nsReady: false` حتى صارت مرئية، ثم جهزت بعد 414 مللي — عند 95% من
 * الخطوة. فلم يكن المزج يحدث، وكان العرض ينقلب عند كل حدّ خطوة: تقليب صورٍ لا حركة.
 *
 * 0.005 تعني ألفا 1 من 255 — لا تراها العين، لكن MapLibre يعدّها طبقةً تُرسم
 * فيُحمّل بلاطاتها. وهي فوق عتبة 0.004 في `setOpacity` فتُكتب فعلاً.
 */
const WAIT_OPACITY = 0.005;
/**
 * عتامة الصدى الكلّية. ملاحظة العميل في 2026-09-21 مقارنةً بـWindy: «الطرق واضحة
 * وبنفس الوقت الألوان». بعتامةٍ كاملة يغطّي الصدى الطرق والحدود فيبدو كتلةً مشوَّشة؛
 * 0.8 تُبقي الألوان مقروءة على المقياس وتُظهر الخريطة من تحتها.
 */
const RADAR_ALPHA = 0.8;
/** عتبة الصدى — تطابق `MIN_DBZ` في mrms_to_raster.py (ما دونها لا يُلوَّن). */
const MIN_DBZ = 5;
type Slot = number;

/*
 * ── لا يمرّ الزمن عبر React ──────────────────────────────────────────────────
 *
 * كان `epoch` خاصّيةً، وهو يتغيّر مع **كل نبضة ساعة** — فيُعاد تصيير المكوّن وتُعاد
 * حسابات النافذة ستّين مرّة في الثانية لتُظهر إطاراً يتغيّر مرّتين. وقِيس أثر ذلك في
 * 2026-09-20: React صار أكبر حاجزٍ للخيط (767 مللي في 15 مرّة = 51 لكل مرّة)، بعد
 * أن زال حاجز MapLibre. ومن أعراضه إطارٌ لم يكتمل رسمه: نصف خريطة وباقيها أسود.
 *
 * والطبقة تقرأ الساعة بنفسها أصلاً (‏subscribePlayback أدناه). فلتقرأ منها **كل
 * شيء**، ولا تُصيَّر إلا حين تتبدّل نافذة الإطارات فعلاً — أي مرّتين في الثانية لا
 * ستّين. و`memo` يقطع تصييرات الأب التي لا تحمل جديداً.
 */
export const RadarDomainTiles = memo(function RadarDomainTiles({ domain, times, beforeId }: {
    domain: RadarDomain; times: readonly number[]; beforeId: string | undefined;
}) {
    const mapRef = useMapRef();
    // نافذة الإطارات المطلوبة — تُحدَّث من الساعة، لا من خاصّية.
    const [wantKey, setWantKey] = useState('');
    const wantRef = useRef<string[]>([]);

    // الاسمان الأوّلان يبقيان كما كانا: مرساتا ترتيبٍ معروفتان في layerOrder.
    const ids = useMemo(() => Array.from({ length: SLOTS }, (_, i) => (
        i === 0 ? { src: `radar-source-${domain.id}`, layer: `radar-layer-${domain.id}` }
            : i === 1 ? { src: `radar-source-${domain.id}-next`, layer: `radar-layer-${domain.id}-next` }
                : { src: `radar-source-${domain.id}-${i}`, layer: `radar-layer-${domain.id}-${i}` }
    )), [domain.id]);

    // ما في كل خانة الآن. مرجعٌ لا حالة: يُقرأ ويُكتب داخل نفس المؤثّر، وجعله حالةً
    // كان سيُعيد تشغيل المؤثّر الذي كتبه.
    const held = useRef<(string | null)[]>(Array(SLOTS).fill(null));
    const [assignKey, setAssignKey] = useState('');

    // النمط قد لا يكون جاهزاً عند أوّل تركيب، والمصدر لا يُضاف قبله. استطلاعٌ قصير
    // كالذي في RadarTileLayer — `mapRef` مرجعٌ ثابت فلا يوقظنا وحده.
    const [styleReady, setStyleReady] = useState(false);
    useEffect(() => {
        let id = 0;
        /*
         * ── «محلَّل» لا «محمَّل» ────────────────────────────────────────────────
         *
         * `isStyleLoaded()` في MapLibre لا تعني «النمط جاهز» بل «النمط **وكل
         * مصادره** محمّلة». والصورة الجوّية تحت الرادار تطلب بلاطاتٍ بلا توقّف ما
         * دام المستخدم ينظر، فتبقى false إلى الأبد — قِيس في 2026-09-21: عشر قراءات
         * متتالية على ثلاث ثوانٍ كلّها false. فلم يكن المكوّن يُضيف مصدراً ولا طبقة،
         * والرادار لا يظهر. وعند من ظهر له كان ذلك صدفةَ لحظة سكون.
         *
         * وشرط MapLibre الحقيقي لإضافة مصدر هو أن يكون النمط **محلَّلاً**
         * (`style._loaded`) — وإلّا يرمي «Style is not done loading». فذلك ما يُسأل.
         */
        const read = () => {
            const gl = mapRef.current?.getMap() as unknown as
                { style?: { _loaded?: boolean }; isStyleLoaded?: () => boolean } | undefined;
            const ok = !!(gl?.style?._loaded || gl?.isStyleLoaded?.());
            if (ok) setStyleReady(true);
            return ok;
        };
        if (!read()) id = window.setInterval(() => { if (read()) window.clearInterval(id); }, 200);
        return () => window.clearInterval(id);
    }, [mapRef]);

    const want = wantRef.current;

    // ── الإسناد إلى الخانات ─────────────────────────────────────────────────
    useEffect(() => {
        const map = mapRef.current?.getMap();
        if (!map || !styleReady) return;

        const { west, east, south, north } = domain.bounds;
        // حدّ المصدر يقصّ الطلبات خارج النطاق. يُترك عند تجاوز خطّ التاريخ
        // (‏west > east) لأن MapLibre يقرأها صندوقاً واحداً فيقلب المعنى.
        const bounds: [number, number, number, number] | undefined =
            west < east ? [west, south, east, north] : undefined;

        /*
         * ── المصدر والطبقة يُضمنان معاً، كلٌّ على حدة ──────────────────────────
         *
         * كان `addLayer(…, beforeId)` يأتي بعد `addSource` بلا حارس. و MapLibre يرفض
         * الإضافة قبل طبقةٍ غير موجودة — والمرساة قد تغيب: النمط يُعاد تنسيقه عند
         * تشغيل الصور الجوّية، وتُنقل طبقات الحدود. فيرمي الاستدعاء ويبقى المصدر
         * **بلا طبقة**، ثم لا يعود شيءٌ لإضافتها: في المرّة التالية يجد الكود المصدرَ
         * قائماً فيكتفي بـ`setTiles` ويخرج.
         *
         * قِيس ذلك مباشرةً في 2026-09-21 على الموقع الحيّ: المصدران موجودان، والبلاطات
         * تُحمَّل بنجاح (1157 طلباً بـ200 في التتبّع)، وطبقة الصدى **غير موجودة في
         * النمط أصلاً**. وهو ما رآه المستخدم: «الرادار اختفى».
         *
         * فالطبقة تُضمن في كل إسناد مستقلّةً عن المصدر، والمرساة لا تُمرَّر إلّا إن
         * كانت موجودة — وإلّا تُضاف في القمّة وينقلها مؤثّر المرساة أدناه حين تظهر.
         */
        const template = domain.values ? radarValuesTemplate : radarTilesTemplate;
        const ensureLayer = (slot: Slot) => {
            const { src, layer } = ids[slot];
            if (map.getLayer(layer) || !map.getSource(src)) return;
            const anchor = beforeId && map.getLayer(beforeId) ? beforeId : undefined;
            try {
                map.addLayer(domain.values ? {
                    // القيم تُستوفى ثم تُلوَّن على معالج الرسوم — راجع radarReliefColor.
                    id: layer, type: 'color-relief', source: src,
                    paint: {
                        'color-relief-opacity': WAIT_OPACITY,
                        'color-relief-color': radarReliefColor(MIN_DBZ, domain.values) as never,
                    },
                } : {
                    id: layer, type: 'raster', source: src,
                    // تبدأ مخفيّة: مؤثّر المزج أدناه يضبطها في أوّل نبضة، فلا يومض إطارٌ
                    // كامل العتامة فوق الآخر بين التركيب والنبضة.
                    paint: { 'raster-opacity': WAIT_OPACITY, 'raster-fade-duration': 0 },
                }, anchor);
            } catch { /* النمط قيد التبديل — الإسناد التالي يعيد المحاولة */ }
        };
        const put = (slot: Slot, prefix: string) => {
            const { src } = ids[slot];
            const existing = map.getSource(src) as { setTiles?: (t: string[]) => void } | undefined;
            if (existing?.setTiles) {
                radarDebugSetTiles(src, prefix);
                existing.setTiles([template(prefix)]);
                ensureLayer(slot);
                return;
            }
            if (existing) return;                 // مصدرٌ بنوعٍ آخر — لا نلمسه
            try {
                const v = domain.values;
                map.addSource(src, v ? {
                    // ‏dBZ = R·step + base: ترميز «custom» يقرأ (R·r + G·g + B·b − shift).
                    // والأخضر والأزرق **ليسا صفراً**: MapLibre يقسم على أصغر المعاملات حين
                    // يرمّز محطّات السلّم (‏packDEMData)، فالصفر يجعلها NaN والطبقة كلّها
                    // بلونٍ واحد (رُصد في اختبار 2026-09-22). والبلاطة رمادية (R=G=B)،
                    // فأثر المعامل الضئيل على القيمة ‏2·255·1e-6 — لا يُرى.
                    type: 'raster-dem', tiles: [template(prefix)], encoding: 'custom',
                    redFactor: v.step, greenFactor: 1e-6, blueFactor: 1e-6, baseShift: -v.base,
                    tileSize: RADAR_VALUES_TILE_SIZE, minzoom: 0, maxzoom: MAX_TILE_Z, ...(bounds ? { bounds } : {}),
                } : {
                    type: 'raster', tiles: [template(prefix)],
                    tileSize: 256, minzoom: 0, maxzoom: MAX_TILE_Z, ...(bounds ? { bounds } : {}),
                });
            } catch { return; }
            ensureLayer(slot);
        };

        const h = held.current;
        for (let i = 0; i < SLOTS; i++) {
            if (h[i] && !want.includes(h[i]!)) h[i] = null;       // خرج من النافذة
            else if (h[i]) ensureLayer(i);                        // باقٍ: تُضمن طبقته
        }
        for (const prefix of want) {
            if (h.includes(prefix)) continue;
            const free = h.indexOf(null);
            if (free < 0) continue;               // لا يحدث: المطلوب بعدد الخانات
            h[free] = prefix;
            put(free, prefix);
        }
        setAssignKey(h.join('|'));
    }, [mapRef, styleReady, wantKey, ids, domain.bounds, domain.values, beforeId]);

    // مرساة الترتيب تصل بعد التركيب أحياناً (النمط يجهز متأخّراً) — تُنقل الطبقتان
    // حينها بدل أن تبقيا فوق التسميات.
    useEffect(() => {
        const map = mapRef.current?.getMap();
        if (!map || !beforeId) return;
        for (let i = 0; i < SLOTS; i++) {
            try {
                if (map.getLayer(ids[i].layer) && map.getLayer(beforeId)) {
                    map.moveLayer(ids[i].layer, beforeId);
                }
            } catch { /* الطبقة قيد الإضافة — الإسناد التالي يضبطها */ }
        }
    }, [mapRef, beforeId, ids, assignKey]);

    // الإزالة عند اختفاء النطاق من الشاشة: بلا هذا تتراكم مصادر لنطاقات لا تُرى،
    // وهو ما كان قصُّ النطاقات يحاول تفاديه أصلاً.
    useEffect(() => {
        const map = mapRef.current?.getMap();
        return () => {
            if (!map) return;
            for (let i = 0; i < SLOTS; i++) {
                try {
                    if (map.getLayer(ids[i].layer)) map.removeLayer(ids[i].layer);
                    if (map.getSource(ids[i].src)) map.removeSource(ids[i].src);
                } catch { /* الخريطة تُفكَّك — لا شيء يُنظَّف */ }
            }
            held.current = Array(SLOTS).fill(null);
        };
    }, [mapRef, ids]);

    // ── المزج على ساعة التشغيل ──────────────────────────────────────────────
    //
    // **الخانة تُقرأ من المرجع في كل نبضة، لا من حالة React.** كان هذا المؤثّر يقرأ
    // `assign` (حالة تُكتب في مؤثّر آخر)، فتقع نبضةٌ يكون فيها الإطار قد تقدّم
    // والإسناد لم يلحق: فتُضاء الخانة التي تحمل الإطار **السابق** بكامل العتامة —
    // ومضةٌ إلى الوراء وسط حركةٍ إلى الأمام، وهي بالضبط ما رصده المستخدم في
    // 2026-09-20 («الحركة تظهر ثم ترجع للخلف»). والمرجع يُكتب ويُقرأ في نفس اللحظة
    // فلا فجوة فيه أصلاً.
    useEffect(() => {
        const map = mapRef.current?.getMap();
        if (!map || !styleReady) return;
        const slotOf = (p: string | null | undefined): Slot | null => {
            if (!p) return null;
            const i = held.current.indexOf(p);
            return i < 0 ? null : i;
        };
        /*
         * ── لا تُكتب قيمةٌ لم تتغيّر ─────────────────────────────────────────
         *
         * `setPaintProperty` ليست إسناداً: تُعدّل النمط وتُعلِم المحرّك أن الطبقة
         * اتّسخت فيُعيد الرسم. وكنتُ أكتب أربعاً في كل نبضة — ستّين مرّة في الثانية —
         * حتى حين لا تتحرّك القيمة (الحلقة متوقّفة، أو الكسر لم يتقدّم بما يُرى).
         *
         * قِيس أثر ذلك على جهاز المستخدم في 2026-09-20 بعد إطفاء أقنعة التغطية:
         * نبضة الساعة نفسها صارت أغلى شيء في الصفحة — 17.8 مللي لكل نبضة
         * (15901 مللي في 894). والعتبة 0.004 دون ما تراه العين على 256 درجة.
         *
         * وتُقرأ القيمة السابقة من هذا الكاش لا من `getPaintProperty`: القراءة من
         * المحرّك في كل نبضة كانت تُضاف إلى الكلفة، ولوضع القياس خاصّةً — فيصير
         * المقياس جزءاً ممّا يقيسه.
         */
        const opacityProp = domain.values ? 'color-relief-opacity' : 'raster-opacity';
        const lastOpacity = new Map<Slot, number>();
        const setOpacity = (slot: Slot | null, v: number) => {
            if (slot === null) return;
            const was = lastOpacity.get(slot);
            if (was !== undefined && Math.abs(was - v) < 0.004) return;
            lastOpacity.set(slot, v);
            try {
                if (map.getLayer(ids[slot].layer)) {
                    if (radarDebugOn) radarDebugOpacity(ids[slot].layer, was ?? v, v);
                    map.setPaintProperty(ids[slot].layer, opacityProp, v);
                }
            } catch { /* الطبقة قيد الإضافة أو الحذف — النبضة التالية تضبطها */ }
        };
        /*
         * ── اسأل عن مصدرٍ موجود فقط ──────────────────────────────────────────
         *
         * ‏`isSourceLoaded` على مُعرِّفٍ غير موجود **لا يرجع false**: يُطلق MapLibre
         * خطأً كاملاً بأثر مكدّس (`There is no tile manager with ID …`). والخانتان
         * الأخيرتان لا تُنشآن إلا حين يتبقّى أمام المؤشّر إطاران، فكان السؤال عنهما
         * يطبع ستّة أخطاء في كل نبضة لكل نطاق.
         *
         * وطباعة الأثر في وحدة التحكّم عمليةٌ باهظة: قِيس على جهاز المستخدم في
         * 2026-09-20 أن ساعة التشغيل هبطت إلى **5.1 نبضة في الثانية** بدل ستّين،
         * وأطول فجوة بلغت 33 ثانية. أي أن التقطيع الذي رآه لم يكن في البلاطات ولا
         * في الشبكة، بل في سؤالٍ خاطئ يُطرح ستّين مرّة في الثانية.
         */
        /*
         * والجاهزية تُسأل كل 120 مللي لا في كل نبضة: `isSourceLoaded` تمرّ على
         * بلاطات المصدر كلّها، ولا تتبدّل إجابتها خلال 16 مللي. نفس المبدأ الذي
         * يتبعه حارس `canAdvance` في useAutoplay وللسبب نفسه.
         */
        const readyCache = new Map<string, { at: number; ok: boolean }>();
        const srcLoaded = (id: string): boolean => {
            const now = performance.now();
            const hit = readyCache.get(id);
            if (hit && now - hit.at < 120) return hit.ok;
            let ok = false;
            if (map.getSource(id)) {
                try { ok = map.isSourceLoaded(id); } catch { ok = false; }
            }
            readyCache.set(id, { at: now, ok });
            return ok;
        };
        const vis = new Map<Slot, boolean>();
        const setVisible = (slot: Slot, on: boolean) => {
            if (vis.get(slot) === on) return;          // لا نكتب ما لم يتغيّر
            vis.set(slot, on);
            try {
                if (map.getLayer(ids[slot].layer)) {
                    map.setLayoutProperty(ids[slot].layer, 'visibility', on ? 'visible' : 'none');
                }
            } catch { /* الطبقة قيد الإضافة — النبضة التالية تضبطها */ }
        };

        /*
         * ── المزج لا يرجع داخل الزوج الواحد ──────────────────────────────────
         * بوابة الجاهزية تُرجع المزج إلى الصفر حين لا يكون التالي محمَّلاً. فإذا
         * جهز في منتصف المقطع قفز الصدى إلى الأمام، ثم ارتدّ عند الخطوة التالية —
         * وهو ما وصفه المستخدم في 2026-09-20: «رجوع في الصورة ثم تقدّم».
         * فنُثبّت أعلى قيمة بلغها الزوج: المزج يتقدّم أو يقف، ولا يتراجع أبداً.
         */
        let pairKey = '';
        let peakK = 0;
        // ترتيب الخانتين في النمط يُسأل مرّة لكل زوج لا لكل نبضة.
        let topFor = '';
        let nextOnTop = true;
        const isAbove = (a: Slot, b: Slot): boolean => {
            try {
                const order = (map as unknown as { getLayersOrder?: () => string[] }).getLayersOrder?.()
                    ?? map.getStyle().layers.map((l) => l.id);
                return order.indexOf(ids[a].layer) > order.indexOf(ids[b].layer);
            } catch { return true; }
        };
        const apply = (p: PlaybackPosition) => {
            radarDebugTick();
            if (radarDebugOn) {
                for (let i = 0; i < SLOTS; i++) {
                    if (srcLoaded(ids[i].src)) radarDebugLoaded(ids[i].src);
                }
            }
            const i = Math.max(0, Math.min(times.length - 1, p.index));
            const a = times[i];
            const b = a === undefined ? a : (times[Math.min(i + 1, times.length - 1)] ?? a);
            // محورٌ فارغ لا يحدث (الطبقة لا تُركَّب بلا أوقات)، والحارس يمنع NaN.
            if (a === undefined) return;
            const now = a + ((b as number) - a) * p.fraction;
            const live = framesAround(domain, Math.floor(now));
            /*
             * النافذة: **السابق** والحاليّ واثنان بعده. والسابق ليس ترفاً: لولاه
             * لكانت الخانة التي تُحرَّر عند كل تقدّم هي خانة الإطار الذي غادر للتوّ،
             * وقد تكون الساعة ما زالت تعرضه للحظة — فيمسحه `setTiles` وهو على
             * الشاشة (ومضة اختفاء عند كل خطوة، رُصدت في 2026-09-20).
             */
            const ci = live.cur ? domain.frames.indexOf(live.cur) : -1;
            if (ci >= 0) {
                const start = Math.max(0, ci - 1);
                const w: string[] = [];
                for (let k = 0; k < SLOTS && start + k < domain.frames.length; k++) {
                    const t = domain.frames[start + k].tiles;
                    if (t) w.push(t);
                }
                const wk = w.join('|');
                if (wk !== wantRef.current.join('|')) {
                    wantRef.current = w;
                    setWantKey(wk);          // تصييرٌ واحد لكل إطار، لا لكل نبضة
                }
            }

            const cs = slotOf(live.cur?.tiles);
            // لم تُركَّب خانته بعد: نُبقي ما يُعرض كما هو. إطفاء المعروض هنا كان
            // سيُظهر فجوةً في الصدى بدل انتقالٍ متّصل.
            // `=== null` لا `!cs`: الخانة الأولى رقمها **صفر**، والفحص الكاذب كان
            // يخرج قبل الرسم فتختفي الطبقة كلّها (رُصد فور نشر الخانات الأربع).
            if (cs === null) return;
            const ns = slotOf(live.next?.tiles);
            // الجاهزية حمولة المصدر: المزج نحو مصدرٍ لم تصل بلاطاته يُظهر الصدى
            // يتلاشى إلى العدم ثم يعود دفعةً — أسوأ من القفزة.
            const key = `${live.cur?.tiles ?? ''}|${live.next?.tiles ?? ''}`;
            if (key !== pairKey) { pairKey = key; peakK = 0; }
            const nsReady = ns !== null && srcLoaded(ids[ns].src);
            const raw = nsReady ? live.blend : 0;
            if (raw > peakK) peakK = raw;
            const k = peakK;
            if (radarDebugOn) {
                const tail = (x: string | null | undefined) => (x ? x.slice(-13) : null);
                radarDebugContext({
                    idx: p.index, frac: +p.fraction.toFixed(3), blend: +live.blend.toFixed(3), k: +k.toFixed(3),
                    cur: tail(live.cur?.tiles), next: tail(live.next?.tiles), cs, ns, nsReady,
                    held: held.current.map(tail),
                });
            }
            /*
             * **وكل خانةٍ ليست من الزوج تُطفأ صراحةً.**
             *
             * كان يُضبط الحاليّ والتالي وحدهما، فحين يتقدّم الإطار ولا تكون للتالي
             * خانة بعد (‏ns = null) تبقى الخانة الثالثة على آخر شفافيةٍ أُعطيت لها —
             * أي إطارٌ **ماضٍ** معلّق بجزء من العتامة تحت الحاليّ. فيُرى الصدى
             * مضاعَفاً بين موضعين: يتقدّم ثم يبدو راجعاً. رصده المستخدم في
             * 2026-09-20 («الألوان تتقدّم وتتأخّر»).
             *
             * الضبط الصريح للثلاثة يجعل ما يُعرض دالّةً للحظة وحدها، لا لبقايا
             * نبضةٍ سابقة — وهو نفس الدرس الذي فرض قراءة الإسناد من المرجع.
             */
            /*
             * ── تُترك كلّها ظاهرة، وتُخفَّف بالشفافية ────────────────────────
             *
             * جُرّب إخفاء ما هو خارج الزوج بـ`visibility: none` (توفيراً للرسم حين
             * كانت الطبقات ثمانين). والنتيجة عكسية: **MapLibre لا يُحمّل بلاطات
             * طبقة مخفيّة أصلاً**، فالخانتان الاحتياطيّتان لم تعودا تُهيَّآن، وحين
             * يأتي دورهما تظهران فارغتين حتى تصل بلاطاتهما — أي أن الإخفاء ألغى
             * المهلة التي وُجدت الخانات الأربع من أجلها. رصده المستخدم في
             * 2026-09-20: «الصورة تختفي كثيراً».
             *
             * وذلك التوفير لم يعد له معنى بعد الفسيفساء: أربع طبقات لا ثمانون،
             * فثمن إبقائها ظاهرة صفرٌ عملياً، ومضرّة إخفائها كاملة.
             */
            /*
             * ── مزجٌ بعتامةٍ كلّية ثابتة ─────────────────────────────────────────
             *
             * كان الحاليّ (1-k) والتالي k. وطبقتان شبه شفّافتين فوق بعض لا تجمعان
             * عتامتيهما: في منتصف الخطوة يصير المجموع 1-(0.5·0.5)=0.75 بدل 1، فتخفت
             * الطبقة كلّها ثم تعود مع كل إطار — نبضٌ يُرى «تقطيعاً» (فيديو العميل
             * 2026-09-21 مقارنةً بـWindy). الصحيح: العليا kA، والسفلى بحيث يبقى
             * المجموع A بالضبط: ‏α_b = (1-k)A / (1-kA).
             */
            let aCur = RADAR_ALPHA, aNext = 0;
            if (ns !== null && k > 0) {
                if (topFor !== key) { topFor = key; nextOnTop = isAbove(ns, cs); }
                const wTop = nextOnTop ? k : 1 - k;
                const aTop = wTop * RADAR_ALPHA;
                const aBot = Math.min(1, (1 - wTop) * RADAR_ALPHA / (1 - aTop));
                if (nextOnTop) { aNext = aTop; aCur = aBot; } else { aCur = aTop; aNext = aBot; }
            }
            for (let i = 0; i < SLOTS; i++) {
                setVisible(i, true);
                if (i === cs) setOpacity(i, Math.max(WAIT_OPACITY, aCur));
                else if (i === ns) setOpacity(i, Math.max(WAIT_OPACITY, aNext));
                else setOpacity(i, WAIT_OPACITY);   // خارج الزوج: شبه شفّافة لتُحمَّل
            }
        };

        /*
         * ── وتُستدعى عند سكون الخريطة أيضاً، لا عند نبض الساعة وحده ──────────
         *
         * الطبقات تُضاف بعتامة صفر ويضيئها أوّل نبض. والاشتراك يُستدعى مرّة عند
         * بدئه — لكن الخانات لم تكن قد أُسندت بعد في تلك اللحظة، فيخرج النداء عند
         * حارس `cs === null` ولا يعود شيء يوقظه: الساعة ساكنة ما دام المستخدم لم
         * يُشغّل الشريط. فكانت الطبقة تبقى خفيّة حتى أوّل ضغطة تشغيل — وهو ما
         * رصده المستخدم في 2026-09-20 («لا تظهر الطبقة إلّا عند تشغيل الـ timeline»).
         *
         * و`idle` هو الحدث الصحيح لا `sourcedata`: يقع بعد أن تستقرّ الطبقات
         * وتصل بلاطاتها، ولا يقع أثناء التشغيل المتّصل أصلاً لأن الخريطة لا تسكن
         * حينها. فالكلفة صفر في الحركة، والإيقاظ مضمون في السكون.
         */
        /*
         * ولا يكفي `idle` وحده: مع الصورة الجوّية لا تسكن الخريطة أبداً ما دام أحدٌ
         * ينظر، فلا يقع الحدث. قِيس في 2026-09-21 بعد إصلاح بوّابة النمط: الخانتان
         * مُضافتان وعتامتهما صفرٌ بعد اثنتي عشرة ثانية. فالمؤثّر يُعاد أيضاً عند كل
         * إسنادٍ للخانات (‏assignKey) — وهي اللحظة التي تنشأ فيها الحاجة أصلاً،
         * سكنت الخريطة أم لم تسكن.
         */
        const onIdle = () => apply(getPlaybackPosition());
        const unsub = subscribePlayback(apply);
        map.on('idle', onIdle);
        return () => { unsub(); map.off('idle', onIdle); };
    }, [mapRef, styleReady, ids, domain, times, assignKey]);

    return null;        // كل شيء أمريّ: لا مصدر ولا طبقة من JSX هنا
});
